import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const SaldoLayer = () => {
    // State Management
    const [mitras, setMitras] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    
    // Additional loading states for specific operations
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [submittingTransaction, setSubmittingTransaction] = useState(false);
    
    // Current mitra and transaction being viewed or edited
    const [currentMitra, setCurrentMitra] = useState(null);
    const [currentTransaction, setCurrentTransaction] = useState({
        mitra_id: '',
        amount: '',  // Changed from 0 to empty string for better form handling
        type: 'deposit', // deposit or withdrawal
        description: '',
        transaction_date: new Date().toISOString().split('T')[0],
        status: 'completed'
    });
    
    // Validation state
    const [formErrors, setFormErrors] = useState({});
    
    // Filter & search state
    const [searchTerm, setSearchTerm] = useState('');
    const [bookingStatusFilter, setBookingStatusFilter] = useState('all');
    const [dateRangeFilter, setDateRangeFilter] = useState({
        startDate: '',
        endDate: ''
    });
    
    // API config
    const API_BASE_URL = 'https://api.tomassage.id/api';
    
    // Fetch mitras with error handling and retries
    const fetchMitras = async (retryCount = 0) => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/mitra`, {
                timeout: 15000 // 15 seconds timeout
            });
            console.log('Fetched mitras:', response.data.data);
            
            setMitras(response.data.data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching mitras:', err);
            // Retry logic for network errors
            if (retryCount < 2 && (err.code === 'ECONNABORTED' || !err.response)) {
                setError('Koneksi lambat, mencoba lagi...');
                setTimeout(() => fetchMitras(retryCount + 1), 2000);
                return;
            }
            setError('Gagal memuat data mitra: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };
    
    // Fetch bookings for a specific mitra with improved error handling
    const fetchMitraBookings = async (mitraId, retryCount = 0) => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/mitra/${mitraId}/bookings`, {
                timeout: 15000 // 15 seconds timeout
            });
            console.log('Fetched bookings:', response.data.data);
            setBookings(response.data.data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching mitra bookings:', err);
            // Retry logic for network errors
            if (retryCount < 2 && (err.code === 'ECONNABORTED' || !err.response)) {
                setError('Koneksi lambat, mencoba lagi...');
                setTimeout(() => fetchMitraBookings(mitraId, retryCount + 1), 2000);
                return;
            }
            setError('Gagal memuat booking mitra: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };
    
    // Get mitra balance with improved error handling
    const fetchMitraBalance = async (mitraId, retryCount = 0) => {
        try {
            setLoadingBalance(true);
            const response = await axios.get(`${API_BASE_URL}/${mitraId}/balance`, {
                timeout: 15000 // 15 seconds timeout
            });
            console.log('Response balance:', response.data.balance_summary.current_balance);
            setLoadingBalance(false);
            return response.data.balance_summary.current_balance || 0;
        } catch (err) {
            console.error('Error fetching mitra balance:', err);
            // Retry once for network errors
            if (retryCount < 1 && (err.code === 'ECONNABORTED' || !err.response)) {
                return fetchMitraBalance(mitraId, retryCount + 1);
            }
            setError('Gagal memuat saldo mitra: ' + (err.response?.data?.message || err.message));
            setLoadingBalance(false);
            return 0;
        }
    };
    
    // Initial fetch
    useEffect(() => {
        fetchMitras();
    }, []);
    
    // Handle mitra selection
    const handleSelectMitra = async (mitra) => {
        // First, set the mitra without balance to show UI faster
        setCurrentMitra(mitra);
        setCurrentTransaction({
            ...currentTransaction,
            mitra_id: mitra.id
        });
        
        // Start fetching bookings
        await fetchMitraBookings(mitra.id);
        
        // Try to fetch balance and update mitra object
        try {
            const balance = await fetchMitraBalance(mitra.id);
            setCurrentMitra(prev => ({
                ...prev,
                balance
            }));
        } catch (err) {
            console.error('Error adding balance to mitra:', err);
        }
    };
    
    // Reset mitra selection
    const resetMitraSelection = () => {
        setCurrentMitra(null);
        setBookings([]);
        resetTransactionForm();
    };
    
    // Reset transaction form and validation errors
    const resetTransactionForm = () => {
        setCurrentTransaction({
            mitra_id: currentMitra ? currentMitra.id : '',
            amount: '',
            type: 'deposit',
            description: '',
            transaction_date: new Date().toISOString().split('T')[0],
            status: 'completed'
        });
        setFormErrors({});
        setError(null);
    };
    
    // Validate transaction form
    const validateTransactionForm = () => {
        const errors = {};
        
        if (!currentTransaction.amount) {
            errors.amount = 'Jumlah wajib diisi';
        } else if (isNaN(parseFloat(currentTransaction.amount)) || parseFloat(currentTransaction.amount) <= 0) {
            errors.amount = 'Jumlah harus berupa angka dan lebih besar dari 0';
        }
        
        if (!currentTransaction.description.trim()) {
            errors.description = 'Deskripsi wajib diisi';
        }
        
        if (!currentTransaction.transaction_date) {
            errors.transaction_date = 'Tanggal transaksi wajib diisi';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    
    // Handle add transaction with improved error handling
    const handleAddTransaction = async (e) => {
        e.preventDefault();
        
        // Validate form first
        if (!validateTransactionForm()) {
            return;
        }
        
        try {
            setError(null);
            setSubmittingTransaction(true);
            
            const transactionData = {
                mitra_id: currentMitra.id,
                amount: parseFloat(currentTransaction.amount),
                transaction_type: currentTransaction.type,
                booking_id: 0,
                description: currentTransaction.description,
                status: currentTransaction.status,
                transaction_date: currentTransaction.transaction_date
            };
            
            console.log('Sending transaction data:', transactionData);
            
            const response = await axios.post(`${API_BASE_URL}/mitra/transactions`, transactionData, {
                headers: {
                    'Content-Type': 'application/json'
                },
                timeout: 20000 // 20 seconds timeout
            });
            
            console.log('Transaction response:', response.data);
            
            if (response.data.success || response.status === 201) {
                // Refresh the mitra data including balance
                const updatedBalance = await fetchMitraBalance(currentMitra.id);
                setCurrentMitra(prev => ({
                    ...prev,
                    balance: updatedBalance
                }));
                
                resetTransactionForm();
                setSuccessMessage('Transaksi berhasil ditambahkan');
                
                // Close modal safely using the close button
                // This approach is more compatible since it doesn't rely on the bootstrap global object
                const closeButton = document.getElementById('addTransactionModalClose');
                if (closeButton) {
                    closeButton.click();
                }
            }
        } catch (err) {
            console.error('Error adding transaction:', err);
            let errorMessage = 'Gagal menambahkan transaksi';
            
            if (err.response) {
                // Server responded with error
                errorMessage += ': ' + (err.response.data?.message || 'Error kode ' + err.response.status);
            } else if (err.request) {
                // Request made but no response received
                errorMessage += ': Tidak ada respons dari server. Periksa koneksi internet Anda.';
            } else {
                // Error setting up request
                errorMessage += ': ' + err.message;
            }
            
            setError(errorMessage);
        } finally {
            setSubmittingTransaction(false);
        }
    };
    
    // View booking details
    const viewBookingDetails = (booking) => {
        // Set current booking for viewing
        setCurrentTransaction({
            id: booking.id,
            amount: booking.harga_service,
            description: `Booking: ${booking.nama_service} (${booking.varian_service})`,
            transaction_date: booking.created_at,
            status: booking.status,
            booking_data: booking // Store the full booking data for reference
        });
    };
    
    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };
    
    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        
        try {
            const options = { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            return new Date(dateString).toLocaleDateString('id-ID', options);
        } catch (err) {
            console.error('Error formatting date:', err);
            return dateString; // Return original if parsing fails
        }
    };
    
    // Filter bookings with improved handling for undefined values
    const filteredBookings = bookings.filter(booking => {
        const bookingStatus = (booking.status || '').toLowerCase();
        const filterStatus = bookingStatusFilter.toLowerCase();
        
        const matchesStatus = filterStatus === 'all' || bookingStatus === filterStatus;
        
        const bookingService = (booking.nama_service || '').toLowerCase();
        const bookingAddress = (booking.alamat_user || '').toLowerCase();
        const bookingId = String(booking.id || '');
        const searchTermLower = searchTerm.toLowerCase();
        
        const matchesSearch = 
            bookingService.includes(searchTermLower) ||
            bookingAddress.includes(searchTermLower) ||
            bookingId.includes(searchTerm);
        
        let matchesDateRange = true;
        if (dateRangeFilter.startDate && dateRangeFilter.endDate) {
            try {
                const bookingDate = new Date(booking.created_at).getTime();
                const startDate = new Date(dateRangeFilter.startDate).getTime();
                const endDate = new Date(dateRangeFilter.endDate).getTime();
                matchesDateRange = bookingDate >= startDate && bookingDate <= endDate;
            } catch (err) {
                console.error('Error filtering dates:', err);
            }
        }
        
        return matchesStatus && matchesSearch && matchesDateRange;
    });
    
    // Filter mitras with null safety
    const filteredMitras = mitras.filter(mitra => {
        const mitraName = (mitra.nama_lengkap || '').toLowerCase();
        const mitraPhone = (mitra.nomor_wa || '');
        const searchTermLower = searchTerm.toLowerCase();
        
        return mitraName.includes(searchTermLower) || mitraPhone.includes(searchTerm);
    });
    
    // Get status color
    const getStatusColor = (status = '') => {
        status = status.toLowerCase();
        if (status === 'completed') return 'bg-success';
        if (status === 'in progress') return 'bg-info';
        if (status === 'pending') return 'bg-warning';
        if (status === 'cancelled') return 'bg-danger';
        return 'bg-secondary';
    };
    
    // Render loading state
    if (loading && mitras.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="ms-3 mb-0">Memuat data...</p>
            </div>
        );
    }
    
    return (
        <>
            <div className="row mb-4">
                <div className="col-12">
                    <h2 className="mb-3">Kelola Saldo & Pesanan Mitra</h2>
                    <div className="card shadow-sm">
                        <div className="card-body p-3 p-md-4">
                            {/* Success Message */}
                            {successMessage && (
                                <div className="alert alert-success alert-dismissible fade show" role="alert">
                                    <Icon icon="mdi:check-circle" className="me-2" />
                                    {successMessage}
                                    <button 
                                        type="button" 
                                        className="btn-close"
                                        onClick={() => setSuccessMessage(null)}
                                        aria-label="Close"
                                    ></button>
                                </div>
                            )}
                            
                            {/* Error Message */}
                            {error && (
                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                    <Icon icon="mdi:alert-circle" className="me-2" />
                                    {error}
                                    <button 
                                        type="button" 
                                        className="btn-close"
                                        onClick={() => setError(null)}
                                        aria-label="Close"
                                    ></button>
                                </div>
                            )}
                            
                            {currentMitra ? (
                                // Show mitra bookings
                                <>
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                                        <button 
                                            className="btn btn-outline-secondary"
                                            onClick={resetMitraSelection}
                                        >
                                            <Icon icon="mdi:arrow-left" className="me-1" />
                                            <span className="d-none d-sm-inline">Kembali ke Daftar Mitra</span>
                                            <span className="d-inline d-sm-none">Kembali</span>
                                        </button>
                                        
                                        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                                            <h4 className="mb-0 text-primary text-break">
                                                {currentMitra.nama_lengkap}
                                            </h4>
                                            <span className="badge bg-info fs-6 p-2">
                                                <Icon icon="mdi:wallet" className="me-1" />
                                                Saldo: {loadingBalance ? 
                                                    <span className="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true"></span> : 
                                                    (currentMitra.balance !== undefined ? formatCurrency(currentMitra.balance) : 'Memuat...')}
                                            </span>
                                        </div>
                                        
                                        <button
                                            className="btn btn-primary"
                                            data-bs-toggle="modal"
                                            data-bs-target="#addTransactionModal"
                                            onClick={resetTransactionForm}
                                        >
                                            <Icon icon="mdi:plus" className="me-1" />
                                            <span className="d-none d-sm-inline">Tambah Transaksi</span>
                                            <span className="d-inline d-sm-none">Transaksi</span>
                                        </button>
                                    </div>
                                    
                                    {/* Filter and Search for Bookings */}
                                    <div className="row mb-4 g-3">
                                        <div className="col-md-4">
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <Icon icon="mdi:magnify" />
                                                </span>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    placeholder="Cari booking..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-8">
                                            <div className="d-flex flex-wrap gap-2">
                                                <select 
                                                    className="form-select"
                                                    style={{ width: 'auto', minWidth: '150px' }}
                                                    value={bookingStatusFilter}
                                                    onChange={(e) => setBookingStatusFilter(e.target.value)}
                                                >
                                                    <option value="all">Semua Status</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="in progress">In Progress</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="cancelled">Cancelled</option>
                                                </select>
                                                
                                                <div className="input-group" style={{ maxWidth: '180px' }}>
                                                    <span className="input-group-text bg-light">Dari</span>
                                                    <input 
                                                        type="date" 
                                                        className="form-control" 
                                                        value={dateRangeFilter.startDate}
                                                        onChange={(e) => setDateRangeFilter({...dateRangeFilter, startDate: e.target.value})}
                                                    />
                                                </div>
                                                
                                                <div className="input-group" style={{ maxWidth: '180px' }}>
                                                    <span className="input-group-text bg-light">Sampai</span>
                                                    <input 
                                                        type="date" 
                                                        className="form-control" 
                                                        value={dateRangeFilter.endDate}
                                                        onChange={(e) => setDateRangeFilter({...dateRangeFilter, endDate: e.target.value})}
                                                    />
                                                </div>
                                                
                                                <button 
                                                    className="btn btn-outline-secondary ms-auto" 
                                                    onClick={() => fetchMitraBookings(currentMitra.id)}
                                                    disabled={loading}
                                                    title="Refresh Data"
                                                >
                                                    {loading ? (
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        <Icon icon="mdi:refresh" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Bookings Table */}
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Tanggal</th>
                                                    <th>Layanan</th>
                                                    <th>Harga</th>
                                                    <th className="d-none d-lg-table-cell">Lokasi</th>
                                                    <th>Status</th>
                                                    <th className="text-end">Aksi</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">
                                                            <div className="spinner-border text-primary" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                            <p className="mt-2 mb-0">Memuat data booking...</p>
                                                        </td>
                                                    </tr>
                                                ) : filteredBookings.length > 0 ? (
                                                    filteredBookings.map(booking => (
                                                        <tr key={booking.id}>
                                                            <td>{booking.id}</td>
                                                            <td>{formatDate(booking.created_at)}</td>
                                                            <td>
                                                                <div>
                                                                    <div className="fw-bold">{booking.nama_service || '-'}</div>
                                                                    <small className="text-muted">{booking.varian_service || '-'}</small>
                                                                </div>
                                                            </td>
                                                            <td className="fw-bold">
                                                                {booking.harga_service ? formatCurrency(booking.harga_service) : '-'}
                                                            </td>
                                                            <td className="d-none d-lg-table-cell">
                                                                <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {booking.alamat_user || '-'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${getStatusColor(booking.status)}`}>
                                                                    {booking.status || 'Unknown'}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => viewBookingDetails(booking)}
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#viewBookingModal"
                                                                >
                                                                    <Icon icon="mdi:eye" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">
                                                            <Icon icon="mdi:calendar-remove" style={{ fontSize: '2rem' }} className="text-secondary" />
                                                            <h6 className="mt-2">Belum ada booking</h6>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                // Show mitra list
                                <>
                                    <h4 className="mb-3">Pilih Mitra</h4>
                                    <div className="row mb-4 g-3">
                                        <div className="col-md-6">
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <Icon icon="mdi:magnify" />
                                                </span>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    placeholder="Cari mitra..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 text-md-end">
                                            <button 
                                                className="btn btn-outline-secondary" 
                                                onClick={fetchMitras}
                                                disabled={loading}
                                                title="Refresh Data"
                                            >
                                                {loading ? (
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    <Icon icon="mdi:refresh" className="me-1" />
                                                )}
                                                Refresh
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="row g-3">
                                        {loading ? (
                                            <div className="col-12 text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="mt-2">Memuat data mitra...</p>
                                            </div>
                                        ) : filteredMitras.length > 0 ? (
                                            filteredMitras.map(mitra => (
                                                <div key={mitra.id} className="col-xl-3 col-lg-4 col-md-6">
                                                    <div className="card h-100 border shadow-sm">
                                                        <div className="card-body text-center p-3">
                                                            <div 
                                                                style={{ 
                                                                    width: '80px', 
                                                                    height: '80px',
                                                                    borderRadius: '50%',
                                                                    margin: '0 auto 15px',
                                                                    backgroundImage: `url(${mitra.foto_profil || 'https://via.placeholder.com/80?text=User'})`,
                                                                    backgroundSize: 'cover',
                                                                    backgroundPosition: 'center'
                                                                }}
                                                            ></div>
                                                            <h5 className="card-title text-primary text-break mb-2">{mitra.nama_lengkap || 'Mitra'}</h5>
                                                            <p className="card-text mb-1">
                                                                <Icon icon="mdi:phone" className="me-1" />
                                                                {mitra.nomor_wa || '-'}
                                                            </p>
                                                            <p className="card-text mb-3">
                                                                <Icon icon="mdi:map-marker" className="me-1" />
                                                                {mitra.alamat ? (mitra.alamat.length > 30 ? mitra.alamat.substring(0, 30) + '...' : mitra.alamat) : '-'}
                                                            </p>
                                                            <button 
                                                                className="btn btn-primary w-100"
                                                                onClick={() => handleSelectMitra(mitra)}
                                                            >
                                                                <Icon icon="mdi:wallet" className="me-1" />
                                                                Kelola Saldo & Pesanan
                                                            </button>
                                                        </div>
                                                        <div className="card-footer bg-transparent p-2">
                                                            <span className={`badge ${mitra.status === 'active' ? 'bg-success' : 'bg-danger'} w-100 p-2`}>
                                                                {mitra.status === 'active' ? 'Aktif' : 'Nonaktif'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12 text-center py-5">
                                                <Icon icon="mdi:account-off" style={{ fontSize: '3rem' }} className="text-secondary" />
                                                <h5 className="mt-3">Tidak ada mitra yang ditemukan</h5>
                                                <p className="text-muted">Coba ubah kata kunci pencarian Anda</p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Add Transaction Modal */}
            <div className="modal fade" id="addTransactionModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Tambah Transaksi</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="addTransactionModalClose"
                            ></button>
                        </div>
                        <form onSubmit={handleAddTransaction}>
                            <div className="modal-body">
                                {/* Show error message in modal if any */}
                                {error && (
                                    <div className="alert alert-danger">
                                        <Icon icon="mdi:alert-circle" className="me-2" />
                                        {error}
                                    </div>
                                )}
                                
                                <div className="mb-3">
                                    <label className="form-label">Tipe Transaksi <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select"
                                        value={currentTransaction.type}
                                        onChange={(e) => setCurrentTransaction({...currentTransaction, type: e.target.value})}
                                        required
                                    >
                                        <option value="deposit">Deposit (Tambah Saldo)</option>
                                        <option value="withdrawal">Penarikan (Kurangi Saldo)</option>
                                    </select>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Jumlah (Rp) <span className="text-danger">*</span></label>
                                    <input
                                        type="number"
                                        className={`form-control ${formErrors.amount ? 'is-invalid' : ''}`}
                                        placeholder="Contoh: 100000"
                                        value={currentTransaction.amount}
                                        onChange={(e) => setCurrentTransaction({...currentTransaction, amount: e.target.value})}
                                        min="1"
                                        required
                                    />
                                    {formErrors.amount && (
                                        <div className="invalid-feedback">
                                            {formErrors.amount}
                                        </div>
                                    )}
                                    <small className="form-text text-muted">
                                        Masukkan angka tanpa titik atau koma
                                    </small>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Deskripsi <span className="text-danger">*</span></label>
                                    <textarea
                                        className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                                        rows="3"
                                        placeholder="Contoh: Penarikan saldo, Bonus bulanan, dll"
                                        value={currentTransaction.description}
                                        onChange={(e) => setCurrentTransaction({...currentTransaction, description: e.target.value})}
                                        required
                                    ></textarea>
                                    {formErrors.description && (
                                        <div className="invalid-feedback">
                                            {formErrors.description}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Tanggal Transaksi <span className="text-danger">*</span></label>
                                    <input
                                        type="date"
                                        className={`form-control ${formErrors.transaction_date ? 'is-invalid' : ''}`}
                                        value={currentTransaction.transaction_date}
                                        onChange={(e) => setCurrentTransaction({...currentTransaction, transaction_date: e.target.value})}
                                        required
                                    />
                                    {formErrors.transaction_date && (
                                        <div className="invalid-feedback">
                                            {formErrors.transaction_date}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Status <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select"
                                        value={currentTransaction.status}
                                        onChange={(e) => setCurrentTransaction({...currentTransaction, status: e.target.value})}
                                        required
                                    >
                                        <option value="completed">Selesai</option>
                                        <option value="pending">Pending</option>
                                        <option value="cancelled">Dibatalkan</option>
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submittingTransaction}
                                >
                                    {submittingTransaction ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Menyimpan...
                                        </>
                                    ) : (
                                        'Simpan'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            
            {/* View Booking Modal */}
            <div className="modal fade" id="viewBookingModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Detail Booking</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {currentTransaction.booking_data && (
                                <div className="card mb-3 border-0">
                                    <div className="card-body">
                                        <div className="text-center mb-4">
                                            <div className="rounded-circle p-3 d-inline-block mb-2 bg-primary bg-opacity-10">
                                                <Icon icon="mdi:spa" 
                                                    style={{ fontSize: '3rem' }} 
                                                    className="text-primary" />
                                            </div>
                                            <h4 className="text-primary text-break">
                                                {currentTransaction.booking_data.nama_service || '-'}
                                            </h4>
                                            <p className="mb-1">{currentTransaction.booking_data.varian_service || '-'}</p>
                                            <h5 className="text-success mb-2">
                                                {currentTransaction.booking_data.harga_service 
                                                    ? formatCurrency(currentTransaction.booking_data.harga_service) 
                                                    : '-'}
                                            </h5>
                                            <span className={`badge ${getStatusColor(currentTransaction.booking_data.status)}`}>
                                                {currentTransaction.booking_data.status || 'Unknown'}
                                            </span>
                                        </div>
                                        
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th style={{ width: '140px' }}>ID Booking</th>
                                                    <td>: {currentTransaction.booking_data.id || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Tanggal Booking</th>
                                                    <td>: {currentTransaction.booking_data.tanggal_booking 
                                                        ? formatDate(currentTransaction.booking_data.tanggal_booking) 
                                                        : '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Alamat User</th>
                                                    <td className="text-break">: {currentTransaction.booking_data.alamat_user || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Gender</th>
                                                    <td>: {currentTransaction.booking_data.gender_select || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Progress</th>
                                                    <td>: {currentTransaction.booking_data.progress_tracking || '-'}</td>
                                                </tr>
                                                {currentTransaction.booking_data.url_maps_user && (
                                                    <tr>
                                                        <th>Lokasi Maps</th>
                                                        <td>: <a href={currentTransaction.booking_data.url_maps_user} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-primary">
                                                                <Icon icon="mdi:map-marker" className="me-1" />
                                                                Lihat di Google Maps
                                                            </a>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SaldoLayer;